import { GridColumnHeaderParams } from "@mui/x-data-grid";
import { ChartDataProps, graficType } from "../../graficos";
import { useRef, useState } from "react";
import { ChromePicker } from "react-color";
import { Box, Button, Checkbox, Divider, IconButton, Input, Menu, MenuItem, Modal, Popover, SxProps, TextField, Tooltip, Typography } from "@mui/material";
import { Theme } from "@mui/system";
import { Add, Check, Close, Delete, TableRows } from "@mui/icons-material";
import { NumberInput } from "../../numberInput";

export interface RenderHeaderProps {
  isOptions?: boolean;
  onRemove?: (colName: string) => void;
  onAdd?: () => void;
  onAddRow?: () => void;
  onTitleChange?: (field: string, title: string) => void;
  onStyleChange?: (field: string, style: ChartDataProps) => void;
  style?: ChartDataProps;
  params: GridColumnHeaderParams;
}

export const RenderHeader: React.FC<RenderHeaderProps> = (data) => {
  const { params, onRemove, onAdd, onAddRow, isOptions, onTitleChange, style, onStyleChange } = data;

  const [anchorTitle, setAnchorTitle] = useState<null | HTMLElement>(null);
  const [text, setText] = useState<string>(params.colDef.headerName ?? "");
  const [itemStyle, setItemStyle] = useState<ChartDataProps | undefined>(style);

  const handleRemoveCol = () => {
    if (onRemove) onRemove(params.field);
  };

  const handleAddCol = () => {
    if (onAdd) onAdd();
  };
  const handleAddRow = () => {
    if (onAddRow) onAddRow();
  };

  const handleClickText = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorTitle(event.currentTarget);
  };
  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setText(event.target.value);
  };
  const handleEditTextFinish = () => {
    if (onTitleChange) onTitleChange(params.field, text);
    setAnchorTitle(null);
  };

  const handleStyle = (style: ChartDataProps) => {
    setItemStyle(style);
    if (onStyleChange) onStyleChange(params.field, style);
  };

  return <Box sx={{
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  }}>

    {/* Descrição da coluna */}
    <>
      {/* Texto da Coluna */}
      {!isOptions && <Typography
        fontWeight={"bold"}
        fontSize={12}
        onClick={handleClickText}
        sx={{ cursor: "pointer" }}
      >
        {params.colDef.headerName}
      </Typography>}

      {/* Menu para alterar o nome da coluna */}
      <Menu
        anchorEl={anchorTitle}
        open={Boolean(anchorTitle)}
        onClose={() => setAnchorTitle(null)}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label="Nome da Coluna"
            value={text}
            onChange={handleTextChange}
            variant="outlined"
            fullWidth
          />
          <IconButton onClick={handleEditTextFinish}>
            <Check />
          </IconButton>
        </Box>
      </Menu>
    </>

    {/* Espaçador */}
    {!isOptions && <div style={{ width: "15px" }} />}

    {/* Icone do estilo */}
    {itemStyle && <StyleSelector
      initialStyle={itemStyle}
      onStyle={handleStyle}
    />}

    {/* Icone Remove Col */}
    {!isOptions && <IconButton onClick={handleRemoveCol}>
      <Delete />
    </IconButton>}

    {/* Icones Add Linha e col */}
    {isOptions && <Tooltip title="Adicionar Linha">
      <IconButton onClick={handleAddRow}>
        <TableRows />
      </IconButton>
    </Tooltip>}
    {isOptions && <Tooltip title="Adicionar Coluna">
      <IconButton onClick={handleAddCol}>
        <Add />
      </IconButton>
    </Tooltip>}
  </Box>;
};


const iconStyle: SxProps<Theme> = {
  fontSize: 18,
  width: 15,
  height: 15,
  padding: 0,
  margin: 0,
  opacity: undefined,
};

interface SelectorProps {
  initialStyle: ChartDataProps;
  onStyle?: (newStyle: ChartDataProps) => void;
}
const StyleSelector: React.FC<SelectorProps> = ({ initialStyle, onStyle }) => {
  const startStyle = useRef(initialStyle);

  const [itemStyle, setItemStyle] = useState<ChartDataProps>(initialStyle);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorTypeGraph, setAnchorTypeGraph] = useState<HTMLElement | null>(null);
  const [anchorFillColor, setAnchorFillColor] = useState<HTMLElement | null>(null);
  const [anchorStrokeColor, setAnchorStrokeColor] = useState<HTMLElement | null>(null);

  const handleStyleFinish = () => {
    if (!itemStyle) return;

    if (onStyle) onStyle(itemStyle);
    setAnchorEl(null);
  };
  const handleOpenStyle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setItemStyle(startStyle.current);
  };

  let newStyle: SxProps = { ...(iconStyle as SxProps) };
  if (itemStyle?.fill) {
    newStyle = {
      ...newStyle,
      backgroundColor: itemStyle.fill,
      border: `${itemStyle.strokeWidth ?? 1}px solid ${itemStyle.stroke ?? '#00000000'}`
    };
  }

  return <>
    {/* Icone que abre o menu */}
    <IconButton onClick={handleOpenStyle}>
      <Box sx={newStyle} />
    </IconButton>

    {/* Menu */}
    <Modal
      open={Boolean(anchorEl)}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <>
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: "50vw",
          height: "60vh",
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          overflowY: "auto",
        }}>
          <div style={{ flex: 1 }}>
            {/* Botão para fechar */}
            <div style={{
              display: "flex",
              width: "100%",
            }}>
              <Typography
                fontWeight={"bold"}
                fontSize={18}
              >{itemStyle.dataKey}</Typography>
              <div style={{ flex: 1 }} />
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </div>
            <Divider />
            <div style={{ height: "15px" }} />

            {/* Tipo de Gráfico */}
            <>
              {/* Menu para o tipo do gráfico */}
              <Menu
                id="simple-menu"
                anchorEl={anchorTypeGraph}
                open={Boolean(anchorTypeGraph)}
                onClose={() => setAnchorTypeGraph(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                {(["bar", "line", "area", "scatter", "pie", "radar"] as graficType[]).map((type, i) => {
                  return <MenuItem
                    key={i}
                    onClick={() => {
                      setItemStyle({ ...itemStyle, typeGraph: type });
                      setAnchorTypeGraph(null);
                    }}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>;
                })}
              </Menu>

              {/* Botão para o tipo do gráfico */}
              <div style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}>
                <Typography>Tipo do Gráfico: </Typography>
                <div style={{ flex: 1 }} />
                <Button
                  aria-controls={anchorTypeGraph ? 'simple-menu' : undefined}
                  aria-haspopup="true"
                  onClick={(event) => setAnchorTypeGraph(event.currentTarget)}
                  variant="contained"
                >
                  {itemStyle.typeGraph.charAt(0).toUpperCase() + itemStyle.typeGraph.slice(1)}
                </Button>
              </div>
            </>
            <div style={{ height: "15px" }} />

            {/* Cor de preenchimento de Gráfico */}
            <>
              {/* Cor do gráfico */}
              <Popover
                open={Boolean(anchorFillColor)}
                anchorEl={anchorFillColor}
                onClose={() => setAnchorFillColor(null)}
              >
                <ChromePicker
                  color={itemStyle.fill}
                  disableAlpha={false}
                  onChange={(color) => setItemStyle({
                    ...itemStyle,
                    fill:
                      color.rgb.a ?
                        `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})` :
                        `rgb(${color.rgb.r},${color.rgb.g},${color.rgb.b})`
                  })}
                />
              </Popover>

              {/* Icone da cor do gráfico */}
              <div style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}>
                <Typography>Cor de preenchimento: </Typography>
                <div style={{ flex: 1 }} />
                <IconButton onClick={(event) => setAnchorFillColor(event.currentTarget)}>
                  <Box sx={{
                    ...newStyle,
                    gcolor: itemStyle.fill,
                    border: `${itemStyle.strokeWidth ?? 1}px solid ${itemStyle.stroke ?? '#00000000'}`
                  }} />
                </IconButton>
              </div>
            </>
            <div style={{ height: "15px" }} />

            {/* Cor da borda do Gráfico */}
            <>
              {/* Cor da borda */}
              <Popover
                open={Boolean(anchorStrokeColor)}
                anchorEl={anchorStrokeColor}
                onClose={() => setAnchorStrokeColor(null)}
              >
                <ChromePicker
                  color={itemStyle.stroke}

                  disableAlpha={false}
                  onChange={(color) => setItemStyle({
                    ...itemStyle,
                    stroke:
                      color.rgb.a ?
                        `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})` :
                        `rgb(${color.rgb.r},${color.rgb.g},${color.rgb.b})`
                  })}
                />
              </Popover>

              {/* Icone da cor do gráfico */}
              <div style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}>
                <Typography>Borda do gráfico: </Typography>
                <div style={{ flex: 1 }} />

                <NumberInput
                  placeholder="Largura da borda"
                  value={itemStyle.strokeWidth}
                  onChange={(event, val) => setItemStyle({ ...itemStyle, strokeWidth: (val && val < 0) ? 0 : (val ?? undefined) })}
                />
                <IconButton onClick={(event) => setAnchorStrokeColor(event.currentTarget)}>
                  <Box sx={{
                    ...newStyle,
                    gcolor: itemStyle.fill,
                    border: `${itemStyle.strokeWidth ?? 1}px solid ${itemStyle.stroke ?? '#00000000'}`
                  }} />
                </IconButton>
              </div>
            </>
            <div style={{ height: "15px" }} />

            {/* Conectar nulos */}
            <>
              {/* Botões */}
              <div style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}>
                <Typography>Conectar nulos: </Typography>
                <div style={{ flex: 1 }} />

                <Checkbox
                  value={itemStyle.connectNulls}
                  onChange={(event, check) => setItemStyle({ ...itemStyle, connectNulls: check })}
                />
              </div>
            </>
            <div style={{ height: "15px" }} />

            {/* Grupo */}
            <>
              {/* Botões */}
              <div style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}>
                <Typography>Grupo: </Typography>
                <div style={{ flex: 1 }} />

                <Input
                  placeholder="Adicionar ao grupo"
                  value={itemStyle.stackId}
                  onChange={(event) => setItemStyle({ ...itemStyle, stackId: event.target.value })}
                />
              </div>
            </>
            <div style={{ height: "15px" }} />

          </div>

          {/* Botão para finalizar */}
          <div style={{
            display: "flex",
            width: "100%",
            justifyContent: "end",
          }}>
            <Button
              variant="text"
              onClick={handleStyleFinish}
            >Feito</Button>
          </div>
        </Box>
      </>
    </Modal>
  </>;
};